<script setup>
  import { computed, onMounted, onUnmounted, ref, watch, nextTick } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useRoleStore } from "@/stores/role";
  import { markWorkspaceFreshLogin } from "@/stores/workspace";

  import api, {
    logout as apiLogout,
    getAccessToken,
    getUserInfo,
    setAccessToken,
    setUserInfo,
  } from "@/lib/api";
  import { publicApi } from "@/lib/api";
  import { clearSurveyPopupClosed } from "@/utils/surveyPopup";
  import { TEXT } from "@/utils/constant";

  import BaseNav from "./BaseNav.vue";
  import MegaMenu from "./MegaMenu.vue";
  import { useWorkspaceStore } from "@/stores/workspace";
  import MyInfoModal from "@/components/MyInfoModal.vue";
  import { AlertModal, HelperText } from "@/components";

  // workspace: 나의 검사 관리 업무 화면 헤더
  //  1행 가운데 검사 안내 · 나의 검사 관리 · 기술지원, 2행은 배정된 시험 탭(1개면 숨김)
  const props = defineProps({
    variant: { type: String, default: "default" },
  });
  const isWorkspace = computed(() => props.variant === "workspace");
  const ws = useWorkspaceStore();

  const EXAM_GUIDE_PATH = {
    MLSRD: "/exam/s-plan/overview",
    LITERACY: "/exam/ai-literacy/overview",
    ESSAY: "/exam/essay/overview",
  };
  const isMembersPage = computed(() => route.meta?.wsTopMenu === "members");

  const moveExamGuide = () => {
    router.push(EXAM_GUIDE_PATH[ws.examType] ?? EXAM_GUIDE_PATH.MLSRD);
  };

  const route = useRoute();
  const router = useRouter();
  const roleStore = useRoleStore();
  const isLoggedIn = ref(false);
  const userInfo = ref({});
  // 기술지원은 교사 전용 — 학생 계정에는 노출하지 않는다
  const isStudent = computed(() => userInfo.value?.role === "stu");

  const moveHome = () => {
    router.push("/");
  };

  const goMyTest = () => {
    if (isLoggedIn.value) {
      router.push("/my-test");
    } else {
      redirectToNaverWorks();
    }
  };

  const redirectToNaverWorks = async () => {
    const authUrl = process.env.VUE_APP_API_LOGIN_URL;

    // Original SAML redirect for production
      window.location.href = authUrl;

    // Check if we're in selee environment (when URL contains /login/login)
    
  };

  const logout = async () => {
    try {
      // 백엔드 로그아웃 API 호출 (동기화)
      await apiLogout();

    } catch (error) {
      console.error("Logout failed:", error);
      // 에러가 발생해도 토큰은 삭제하고 홈으로 이동
      localStorage.removeItem("accessToken");
      updateLoginStatus();
      await router.push("/");
    }
  };

  // 남은 로그인 시간이 0이 되면 자동 로그아웃
  // SAML SLO(apiLogout)는 자동 호출하지 않는다 — IdP 세션까지 파기돼 이후 로그인이 loginFail 로 빠짐(api.js doRefresh 참고)
  async function updateLoginStatus() {
    const token = localStorage.getItem("accessToken");
    isLoggedIn.value = !!token;
    if (token) {
      // Force update userInfo and role
      userInfo.value = getUserInfo();
      roleStore.setRoleFromUserInfo();
    } else {
      roleStore.clearRole();
    }
  }

  async function updateUserInfo() {
    userInfo.value = getUserInfo();
    // Also update role when userInfo changes
    if (isLoggedIn.value) {
      roleStore.setRoleFromUserInfo();
    }
  }

  const checkLoginStatus = async () => {
    const token = getAccessToken();

    if (token) {
      isLoggedIn.value = true;
      userInfo.value = getUserInfo();

      // Wait for next tick to ensure userInfo is properly set
      await nextTick();
      roleStore.setRoleFromUserInfo();
    } else {
      isLoggedIn.value = false;
      userInfo.value = {};
      roleStore.clearRole();
    }
  };

  const isOpenMyInfoModal = ref(false);
  const openMyInfoModal = () => {
    isOpenMyInfoModal.value = true;
  };

  // 비밀번호 변경 모달 관련
  const isOpenPwdChangeModal = ref(false);
  const isOpenAlertModal = ref(false);
  const isPasswordLoading = ref(false);
  const newPassword = ref("");
  const newPasswordCheck = ref("");

  // AI 맞춤형 교수학습 플랫폼 비밀번호 변경 모달 관련
  const isOpenGotoAIEPPasswordChangeModal = ref(false);

  const openPwdChangeModal = () => {
    // 모달 열기 전에 입력 필드 초기화
    newPassword.value = "";
    newPasswordCheck.value = "";
    isOpenPwdChangeModal.value = true;
  };

  const cancelPwdChangeModal = () => {
    isOpenPwdChangeModal.value = false;
    newPassword.value = "";
    newPasswordCheck.value = "";
  };

  // AI 맞춤형 교수학습 플랫폼 비밀번호 변경 모달 관련
  const openGotoAIEPPasswordChangeModal = () => {
    isOpenGotoAIEPPasswordChangeModal.value = true;
  };

  const cancelGotoAIEPPasswordChangeModal = () => {
    isOpenGotoAIEPPasswordChangeModal.value = false;
  };

  // AI 맞춤형 교수학습 플랫폼으로 이동
  const gotoAIEPPasswordChange = () => {
    const aiPlatformUrl = 'http://senedu.kr/mainStdntMd.html';
    window.open(aiPlatformUrl, '_blank');
    isOpenGotoAIEPPasswordChangeModal.value = false;
  };

  // 비밀번호 변경 처리
  const handleChangePassword = () => {
     /* openPwdChangeModal();  */ // 추후 수정을 대비하여 모달 살려둠 주석만해제하면됨 (둘중에 하나는 무조건 주석처리)
     openGotoAIEPPasswordChangeModal(); 
  };

  // 비밀번호 유효성 검증 함수
  const validatePassword = () => {
    // 새 비밀번호 일치 확인
    if (newPassword.value !== newPasswordCheck.value) {
      alert('새 비밀번호가 일치하지 않습니다.');
      return false;
    }

    // 비밀번호 길이 및 조건 확인
    const password = newPassword.value;
    const minLength = 10;
    
    // 영문, 숫자, 특수기호 패턴
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[~!@#$%^&*()\-=+_]/.test(password);
    const validLength = password.length >= minLength;
    
    // 최소 2가지 타입 포함 및 10자 이상
    const typeCount = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;
    
    if (!validLength || typeCount < 2) {
      alert('새 비밀번호는 영문, 숫자, 특수기호 중 2개 이상을 포함하여 10자 이상 입력해주세요.');
      return false;
    }

    return true;
  };

  const submitPwdChange = async () => {
    // 유효성 검증
    if (!validatePassword()) {
      return;
    }

    isPasswordLoading.value = true;
    try {
      // 사용자 정보 가져오기
      const userInfo = getUserInfo();
      let apiUrl = "";
      let apiParams = {};

      // 역할별 API 호출 분기
      if (userInfo.role === 'tea') {
        // 교사용 API (기존 ResetTeaPasswordButton 참고)
        apiUrl = "/myTest/initTeaPassword";
        apiParams = {
          email: userInfo.email, // 교사 이메일
          password: newPassword.value,
        };
      } else if (userInfo.role === 'stu') {
        // 학생용 API (기존 ResetPasswordButton 참고)
        apiUrl = "/myTest/initPassword";
        apiParams = {
          gradeCode: userInfo.grade,
          schoolCls: userInfo.classroom,
          schoolNum: userInfo.schoolNum || userInfo.school_num,
          password: newPassword.value,
        };
      } else {
        throw new Error('지원되지 않는 사용자 역할입니다.');
      }

      const { data } = await api.post(apiUrl, apiParams);

      if (data.success) {
        openAlertModal();
      } else {
        throw new Error(data.message || "비밀번호 변경에 실패했습니다.");
      }
    } catch (error) {
      console.error("비밀번호 변경 API 호출 실패:", error);
      alert(
        error.response?.data?.errorMessage ||
          error.message ||
          "비밀번호 변경에 실패했습니다.",
      );
    } finally {
      isPasswordLoading.value = false;
    }
  };

  const openAlertModal = () => {
    isOpenPwdChangeModal.value = false;
    isOpenAlertModal.value = true;
  };

  const submitAlertModal = () => {
    isOpenAlertModal.value = false;
  };

  // 개발 환경 체크
  const isDevelopmentEnvironment = () => {
    const nodeEnv = process.env.NODE_ENV;
    return nodeEnv === 'development' || nodeEnv === 'local';
  };

  // 개발용 ID 입력 팝업 관련
  const isOpenDevModal = ref(false);
  const devInputId = ref('');

  // 개발용 버튼 클릭 핸들러
  const handleDevButton = () => {
    isOpenDevModal.value = true;
    devInputId.value = ''; // 입력값 초기화
  };

  // 개발용 팝업 닫기
  const closeDevModal = () => {
    isOpenDevModal.value = false;
    devInputId.value = '';
  };

  // 개발용 ID 확인 처리
  const handleDevIdConfirm = async () => {
    if (!devInputId.value.trim()) {
      alert('ID를 입력해주세요.');
      return;
    }
    
    try {
      // Make POST request for selee environment
      // withCredentials: 응답의 rt(리프레시 토큰) 쿠키를 저장해야 [연장하기]·토큰 자동 갱신(/oauth/refresh)이 동작한다
      const response = await publicApi.post(
        "/login/login",
        { email: devInputId.value.trim() },
        { withCredentials: true },
      );

      const { accessToken, refreshToken, user } = response.data;

      if (accessToken) {
        // Set tokens and user info
        setAccessToken(accessToken);
        // dev 로그인 응답(AiepUserInfo)은 동의 여부를 isConsentYn으로만 내려주므로,
        // SSO(/oauth/refresh)와 같은 isAgreedValid 키로 맞춰 둔다 (없으면 동의 팝업이 매번 뜸)
        setUserInfo({ ...user, isAgreedValid: user?.isConsentYn });
        // 새로 로그인했으므로 설문 팝업 [닫기] 기록 초기화
        clearSurveyPopupClosed();
        // 나의 검사 관리 첫 진입 때 시험 시작일이 가장 가까운 회차를 미리 선택
        markWorkspaceFreshLogin();

        // Update login status
        updateLoginStatus();

        // Navigate to home
        await router.push("/");
      } else {
        throw new Error("No accessToken in response");
      }
    } catch (error) {
      console.error("Login failed:", error);
      alert("로그인 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
    }
        
    // 팝업 닫기
    closeDevModal();
  };

  // 권한 배지 문구
  const roleLabel = computed(() => {
    if (userInfo.value?.role === "stu") return "학생";
    if (userInfo.value?.role === "tea") return "교사";
    return "사용자";
  });

  // 내 계정 메뉴(US-9800): 이름(권한)·이메일, 나의 정보·비밀번호 변경·로그아웃
  const isOpenAccount = ref(false);
  const accountRef = ref(null);
  const toggleAccount = () => {
    isOpenAccount.value = !isOpenAccount.value;
    if (isOpenAccount.value) closeMega();
  };
  const closeAccount = () => {
    isOpenAccount.value = false;
  };

  const handleDropdownSelect = async (key) => {
    closeAccount();
    if (key === "myInfo") {
      openMyInfoModal();
    }
    if (key === "changePassword") {
      handleChangePassword();
    }
    if (key === "logout") {
      await logout();
    }
  };

  // 전체 메뉴(메가 메뉴): GNB 2행·메뉴 위 호버, ☰ 토글, [기술지원] 클릭 시 열림
  const isOpenMega = ref(false);
  const headerRef = ref(null);
  let megaHoverTimer = null;

  const openMega = () => {
    clearTimeout(megaHoverTimer);
    isOpenMega.value = true;
    closeAccount();
  };
  const closeMega = () => {
    clearTimeout(megaHoverTimer);
    isOpenMega.value = false;
  };
  const toggleMega = () => {
    if (isOpenMega.value) closeMega();
    else openMega();
  };
  // 2행 → 메뉴로 마우스를 옮기는 사이 닫히지 않도록 살짝 지연
  const scheduleCloseMega = () => {
    clearTimeout(megaHoverTimer);
    megaHoverTimer = setTimeout(() => {
      isOpenMega.value = false;
    }, 150);
  };

  // 헤더 바깥 클릭 시 메가 메뉴, 계정 메뉴 바깥 클릭 시 계정 메뉴를 닫는다
  // (본문에 @click.stop 인 요소들이 있어 버블링에 의존하지 않도록 캡처 단계에서 감지)
  const handleDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (isOpenMega.value && !headerRef.value?.contains(target)) closeMega();
    if (isOpenAccount.value && !accountRef.value?.contains(target)) closeAccount();
  };

  const handleDocumentKeydown = (event) => {
    if (event.key === "Escape") {
      closeMega();
      closeAccount();
    }
  };

  // storage 이벤트 핸들러 (탭 간 동기화)
  const handleStorageChange = (e) => {
    if (e.key === 'accessToken') {
      if (e.newValue === null) {
        // 토큰이 삭제됨 (다른 탭에서 로그아웃)
        updateLoginStatus();
        
        // 현재 페이지가 인증이 필요한 페이지라면 홈으로 리다이렉트
        if (route.path.startsWith('/my-test')) {
          router.push('/');
        }
      } else if (e.oldValue === null && e.newValue) {
        // 토큰이 추가됨 (다른 탭에서 로그인)
        updateLoginStatus();
      }
    } else if (e.key === 'user') {
      // iOS WebKit에서 user 저장값이 accessToken보다 늦게 동기화되는 경우 반영
      checkLoginStatus();
    }
  };

  onMounted(() => {
    checkLoginStatus();
    
    // storage 이벤트 리스너 등록 (탭 간 동기화)
    window.addEventListener('storage', handleStorageChange);

    // 같은 탭에서 토큰/유저정보가 재조회된 경우(ensureAuth → doRefresh) 반영
    // (로그인 여부·유저정보·권한을 메모리 캐시 기준으로 함께 다시 읽도록 checkLoginStatus 사용)
    window.addEventListener('auth:refreshed', checkLoginStatus);

    // 사이트맵 바깥 클릭/ESC 로 닫기
    // (본문에 @click.stop 인 요소들이 있어 버블링에 의존하지 않도록 캡처 단계에서 감지)
    document.addEventListener("click", handleDocumentClick, true);
    document.addEventListener("keydown", handleDocumentKeydown);
  });

  onUnmounted(() => {
    // storage 이벤트 리스너 제거
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener('auth:refreshed', checkLoginStatus);

    document.removeEventListener("click", handleDocumentClick, true);
    document.removeEventListener("keydown", handleDocumentKeydown);
  });

  // Watch for route changes (including after login redirect)
  watch(
    () => route.path,
    async () => {
      // 페이지가 바뀌면 전체 메뉴·계정 메뉴는 항상 닫는다
      closeMega();
      closeAccount();
      updateLoginStatus();
      updateUserInfo();
      // Additional check after route change
      await nextTick();
      if (getAccessToken()) {
        roleStore.setRoleFromUserInfo();
      }
    },
  );

  // Watch for login status changes
  watch(
    () => getAccessToken(),
    async () => {
      await checkLoginStatus();
    },
  );

  // Watch for localStorage changes (for cross-tab login detection)
  watch(
    () => localStorage.getItem("accessToken"),
    async () => {
      await checkLoginStatus();
    },
  );
</script>

<template>
  <header ref="headerRef" class="nh" :class="{ 'nh--ws': isWorkspace }">
    <div class="nh-container nh-1">
      <h1
        @click="moveHome"
        @keydown.enter="moveHome"
        @keydown.space.prevent="moveHome"
        class="nh-logo"
        tabindex="0"
        role="button"
        aria-label="홈으로 이동"
      >
        <img src="@/assets/images/logo_header.png" :alt="TEXT.SYSTEM_NAME" />
      </h1>

      <!-- 메뉴 위치: 메인 · 업무 화면 모두 오른쪽 (기술지원 · 구성원 관리 · 나의 검사 관리) -->
      <div class="nh-r">
        <template v-if="!isWorkspace || isLoggedIn">
          <!-- 업무 화면은 2행이 시험 선택이라 검사 안내를 1행에 둔다 -->
          <button v-if="isWorkspace" type="button" class="nh-link" @click="moveExamGuide">검사 안내</button>
          <button type="button" class="nh-link" @click.stop="openMega">기술지원</button>
          <!-- 학교관리자: 구성원 관리 (업무 화면 1단 메뉴와 같은 화면) -->
          <button
            v-if="isLoggedIn && roleStore.isSchoolAdmin"
            type="button"
            class="nh-link"
            @click="router.push({ name: 'MyTestTeacherManageMember' })"
          >
            구성원 관리
          </button>
          <!-- 나의 검사 관리: 로그인 전엔 연한 채움, 로그인 후엔 채움 -->
          <button
            type="button"
            class="nh-pill"
            :class="isLoggedIn ? 'is-primary' : 'is-line'"
            @click="goMyTest"
          >
            나의 검사 관리
          </button>
        </template>

        <template v-if="!isLoggedIn">
          <button type="button" class="nh-pill is-primary" @click="redirectToNaverWorks">
            로그인
          </button>
          <!-- 개발 환경용 버튼 -->
          <button
            v-if="isDevelopmentEnvironment()"
            type="button"
            class="nh-dev"
            @click="handleDevButton"
            aria-label="개발자 도구"
          >
            DEV
          </button>
        </template>

        <template v-else>
          <span class="nh-bar" aria-hidden="true"></span>
          <div ref="accountRef" class="nh-user">
            <button
              type="button"
              :aria-expanded="isOpenAccount"
              aria-haspopup="true"
              aria-label="내 계정 메뉴"
              @click="toggleAccount"
            >
              <span class="nm">
                <small v-if="userInfo.schoolName">{{ userInfo.schoolName }}</small>
                <span>
                  <span class="nh-role">{{ roleLabel }}</span>
                  <b>{{ userInfo.name || "사용자" }} 님</b>
                </span>
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="m2.5 4.5 3.5 3.5 3.5-3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <div v-show="isOpenAccount" class="nh-acct" role="menu">
              <div class="who">
                <b>{{ userInfo.name || "사용자" }} 님({{ roleLabel }})</b>
                <span>{{ userInfo.email }}</span>
              </div>
              <button type="button" role="menuitem" @click="handleDropdownSelect('myInfo')">
                나의 정보
              </button>
              <!-- 학교 관리자는 비밀번호 변경 메뉴 없음 -->
              <button
                v-if="!roleStore.isSchoolAdmin"
                type="button"
                role="menuitem"
                @click="handleDropdownSelect('changePassword')"
              >
                비밀번호 변경
              </button>
              <button type="button" role="menuitem" @click="handleDropdownSelect('logout')">
                로그아웃
              </button>
            </div>
          </div>
        </template>

        <button
          type="button"
          class="nh-all"
          :aria-expanded="isOpenMega"
          :aria-label="isOpenMega ? '전체 메뉴 닫기' : '전체 메뉴 열기'"
          @click.stop="toggleMega"
        >
          <svg v-if="!isOpenMega" width="22" height="22" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="m5 5 10 10M15 5 5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 업무 화면 2행: 배정된 시험만 표시, 1개면 숨김 -->
    <nav v-if="isWorkspace" v-show="ws.assignedExams.length > 1 && !isMembersPage" class="nh-ws2" aria-label="시험 선택">
      <a
        v-for="exam in ws.assignedExams"
        :key="exam.type"
        href="#none"
        :class="{ 'is-active': exam.type === ws.examType }"
        :aria-current="exam.type === ws.examType ? 'true' : undefined"
        @click.prevent="ws.setExamType(exam.type)"
      >
        {{ exam.label }}
      </a>
    </nav>
    <div v-else class="nh-2wrap" @mouseenter="openMega" @mouseleave="scheduleCloseMega">
      <div class="nh-container">
        <BaseNav @navigate="closeMega" />
      </div>
    </div>

    <MegaMenu
      v-if="isOpenMega"
      :is-logged-in="isLoggedIn"
      :is-student="isStudent"
      :is-workspace="isWorkspace"
      @mouseenter="openMega"
      @mouseleave="scheduleCloseMega"
      @close="closeMega"
      @require-login="redirectToNaverWorks"
    />
  </header>

  <MyInfoModal v-model:show="isOpenMyInfoModal" />

  <!-- 비밀번호 변경 모달 -->
  <ui-modal
    v-model:show="isOpenPwdChangeModal"
    title="비밀번호 변경"
    size="sm"
  >
    <template #body>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            새 비밀번호
          </label>
          <n-input
            type="password"
            show-password-on="mousedown"
            placeholder="새 비밀번호를 입력하세요"
            size="small"
            v-model:value="newPassword"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            새 비밀번호 확인
          </label>
          <n-input
            type="password"
            show-password-on="mousedown"
            placeholder="새 비밀번호를 다시 입력하세요"
            size="small"
            v-model:value="newPasswordCheck"
          />
        </div>
        <helper-text :icon="null" class="text-sm text-gray-600">
          영문/숫자/특수기호 2개 이상 조합으로 10글자 이상
        </helper-text>
      </div>
    </template>
    <template #footer>
      <div class="btn-area justify-end">
        <ui-button
          color="gray"
          variant="outlined"
          @click="cancelPwdChangeModal"
          :disabled="isPasswordLoading"
        >
          취소
        </ui-button>
        <ui-button @click="submitPwdChange" :loading="isPasswordLoading">
          확인
        </ui-button>
      </div>
    </template>
  </ui-modal>

    <ui-modal
    v-model:show="isOpenGotoAIEPPasswordChangeModal"
    title="비밀번호 변경"
    size="sm"
  >
        <template #body>
      <div class="space-y-4">
                <div class="text-gray-700">
          <p class="mb-3">비밀번호는 AI 맞춤형 교수학습 플랫폼에서 변경 가능합니다.</p>
          <p class="mb-3">비밀번호를 변경하시겠습니까?</p>
          <p class="text-sm text-gray-500">* AI 맞춤형 교수학습 플랫폼으로 이동합니다.</p>
        </div>
      </div>
    </template>
    <template #footer>
            <div class="btn-area justify-end">
        <ui-button
          color="gray"
          variant="outlined"
                    @click="cancelGotoAIEPPasswordChangeModal"
        >
                  취소
        </ui-button>
                <ui-button @click="gotoAIEPPasswordChange">
          확인
        </ui-button>
      </div>
    </template>
  </ui-modal>

  <!-- 비밀번호 변경 완료 알림 모달 -->
  <AlertModal
    v-model:show="isOpenAlertModal"
    title="비밀번호 변경"
    @submit="submitAlertModal"
  >
    비밀번호가 성공적으로 변경되었습니다.<br />
    다음 로그인부터 새 비밀번호를 사용하세요.
  </AlertModal>

  <!-- 개발용 ID 입력 모달 -->
  <ui-modal v-model:show="isOpenDevModal" title="개발자 도구" size="xs">
    <template #body>
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            ID 입력
          </label>
          <n-input
            v-model:value="devInputId"
            placeholder="ID를 입력하세요"
            @keydown.enter="handleDevIdConfirm"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end space-x-3">
        <ui-button
          variant="outlined"
          color="gray"
          @click="closeDevModal"
        >
          닫기
        </ui-button>
        <ui-button
          color="primary"
          @click="handleDevIdConfirm"
        >
          확인
        </ui-button>
      </div>
    </template>
  </ui-modal>
</template>

<style lang="scss" scoped>
  .nh {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    min-width: var(--smoe-content-w);
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: saturate(1.4) blur(8px);
    box-shadow: 0 1px 0 #e2e6ea;
    z-index: 40;

    :where(button) {
      font: inherit;
      color: inherit;
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
    }
  }

  .nh-container {
    position: relative;
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
  }

  /* 1행: 로고 · 유틸 */
  .nh-1 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    height: 68px;
  }

  .nh-logo {
    display: flex;
    align-items: center;
    cursor: pointer;

    img {
      height: 26px;
      width: auto;
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--smoe-primary-500);
      outline-offset: 2px;
      border-radius: 4px;
    }
  }

  .nh-r {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .nh-link {
    display: inline-flex;
    align-items: center;
    height: 40px;
    padding: 0 16px !important;
    border-radius: 60px;
    font-size: 15px;
    font-weight: 600;
    color: #464c53 !important;
    white-space: nowrap;

    &:hover {
      background: #f5f7fa !important;
      color: #1e2124 !important;
    }
  }

  .nh-pill {
    display: inline-flex;
    align-items: center;
    height: 40px;
    padding: 0 20px !important;
    border-radius: 60px;
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;
    transition: background 0.15s;

    &.is-primary {
      background: #0a7cff !important;
      color: #fff !important;
      box-shadow: 0 4px 10px rgba(10, 124, 255, 0.28);

      &:hover {
        background: #0060d4 !important;
      }
    }

    &.is-line {
      background: #e9f3ff !important;
      color: #0a7cff !important;

      &:hover {
        background: #dcebff !important;
      }
    }
  }

  .nh-dev {
    height: 36px;
    padding: 0 14px !important;
    border: 1px solid #d7dae6 !important;
    border-radius: 60px;
    font-size: 13px;
    font-weight: 500;
    color: #4a4f63 !important;
  }

  .nh-bar {
    width: 1px;
    height: 18px;
    margin: 0 6px;
    background: #e2e6ea;
  }

  /* 내 계정 */
  .nh-user {
    position: relative;

    > button {
      display: flex;
      align-items: center;
      gap: 10px;
      height: 42px;
      padding: 0 8px !important;
      border-radius: 10px;

      &:hover,
      &[aria-expanded="true"] {
        background: #f5f7fa !important;
      }
    }

    .nm {
      display: flex;
      flex-direction: column;
      text-align: left;
      line-height: 1.25;

      small {
        font-size: 11px;
        color: #8a949e;
      }

      > span {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      b {
        font-size: 14px;
        color: #1e2124;
        white-space: nowrap;
      }
    }
  }

  .nh-role {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 6px;
    background: #eaf3ff;
    color: #2a64c8;
    font-size: 12px;
    font-weight: 600;
  }

  .nh-acct {
    position: absolute;
    right: 0;
    top: calc(100% + 8px);
    width: 220px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #e2e6ea;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
    z-index: 60;

    .who {
      padding: 16px;
      text-align: center;
      border-bottom: 1px solid #f0f2f4;

      b {
        display: block;
        font-size: 14px;
        color: #1e2124;
      }

      span {
        font-size: 12px;
        color: #8a949e;
        word-break: break-all;
      }
    }

    button {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      padding: 10px 16px !important;
      font-size: 13px;
      color: #1e2124 !important;
      text-align: left;

      &:hover,
      &:focus-visible {
        background: #f5f7fa !important;
      }
    }
  }

  .nh-all {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    margin-left: 2px;
    border-radius: 10px;
    color: #1e2124 !important;
    transition: background 0.15s;

    &:hover {
      background: #f5f7fa !important;
    }

    &[aria-expanded="true"] {
      background: #1e2124 !important;
      color: #fff !important;
    }
  }

  /* 업무 화면: 메인과 같은 폭 · 같은 메뉴 위치 (오른쪽) */

  .nh-ws2 {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    height: 48px;
    border-top: 1px solid #f0f2f4;

    a {
      display: flex;
      align-items: center;
      height: 32px;
      padding: 0 16px;
      border-radius: 60px;
      font-size: 14px;
      font-weight: 500;
      color: #464c53;
      white-space: nowrap;

      &:hover {
        background: #f5f7fa;
        color: #1e2124;
      }

      &.is-active {
        background: #e9f3ff;
        color: #0a7cff;
        font-weight: 700;
      }
    }
  }

  /* 2행: 검사 3종 */
  .nh-2wrap {
    border-top: 1px solid #f0f2f4;

    .nh-container {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 60px;
    }
  }
</style>
